/**
 * PDF Utility Functions
 * Handles PDF rendering, cropping, and manipulation using pdf.js and jsPDF
 */

let pdfjsLib = null;

/**
 * Initialize pdf.js library from CDN (lazy load)
 */
async function initPdfJs() {
  if (pdfjsLib) return;

  // Load from CDN
  if (typeof window !== "undefined" && !window.pdfjsWorker) {
    // Load pdf.js from CDN
    await new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  pdfjsLib = window.pdfjsLib;

  // Set up the worker from CDN
  pdfjsLib.GlobalWorkerOptions.workerSrc =
    "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
}

/**
 * Render a single PDF page to canvas
 * @param {PDFPageProxy} page - PDF page object
 * @param {number} scale - Rendering scale factor
 * @returns {Promise<HTMLCanvasElement>} Canvas containing rendered page
 */
export async function renderPageToCanvas(page, scale = 2) {
  await initPdfJs();
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({
    canvasContext: context,
    viewport: viewport
  }).promise;

  return canvas;
}

/**
 * Crop a region from a canvas
 * @param {HTMLCanvasElement} canvas - Source canvas
 * @param {Object} cropRegion - Crop region {x, y, width, height} in pixels
 * @returns {string} Data URL of cropped image
 */
export function cropCanvasRegion(canvas, cropRegion) {
  const croppedCanvas = document.createElement("canvas");
  const croppedContext = croppedCanvas.getContext("2d");

  croppedCanvas.width = cropRegion.width;
  croppedCanvas.height = cropRegion.height;

  croppedContext.drawImage(
    canvas,
    cropRegion.x,
    cropRegion.y,
    cropRegion.width,
    cropRegion.height,
    0,
    0,
    cropRegion.width,
    cropRegion.height
  );

  return croppedCanvas.toDataURL("image/png");
}

/**
 * Load PDF document from blob
 * @param {Blob} pdfBlob - PDF file blob
 * @returns {Promise<PDFDocumentProxy>} PDF document object
 */
export async function loadPdfFromBlob(pdfBlob) {
  await initPdfJs();
  const arrayBuffer = await pdfBlob.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  return pdf;
}

/**
 * Get PDF page count
 * @param {PDFDocumentProxy} pdfDoc - PDF document
 * @returns {number} Number of pages
 */
export function getPdfPageCount(pdfDoc) {
  return pdfDoc.numPages;
}

/**
 * Calculate crop region for waybill by trimming whitespace and keeping only actual content.
 * Examines the rendered canvas pixels to detect non-white content bounds.
 * @param {HTMLCanvasElement} canvas - Rendered PDF page canvas
 * @param {Object} options - Crop options
 * @param {number} options.threshold - Pixel brightness threshold for content (0-255)
 * @param {number} options.padding - Extra padding to include around detected content in pixels
 * @returns {Object} Crop region {x, y, width, height}
 */
export function calculateWaybillCropRegion(canvas, options = {}) {
  const { threshold = 245, padding = 8 } = options;

  const ctx = canvas.getContext("2d");
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  let minX = canvas.width;
  let minY = canvas.height;
  let maxX = -1;
  let maxY = -1;

  const isContentPixel = (r, g, b, a) => {
    if (a === 0) {
      return false;
    }

    return r < threshold || g < threshold || b < threshold;
  };

  for (let y = 0; y < canvas.height; y++) {
    const rowStart = y * canvas.width * 4;
    for (let x = 0; x < canvas.width; x++) {
      const idx = rowStart + x * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const a = data[idx + 3];

      if (isContentPixel(r, g, b, a)) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  if (maxX < 0 || maxY < 0) {
    return {
      x: 0,
      y: 0,
      width: canvas.width,
      height: canvas.height
    };
  }

  const x = Math.max(0, minX - padding);
  const y = Math.max(0, minY - padding);
  const width = Math.min(canvas.width - x, maxX - x + padding + 1);
  const height = Math.min(canvas.height - y, maxY - y + padding + 1);

  return { x, y, width, height };
}

/**
 * Scale image to fit thermal label dimensions while maintaining aspect ratio
 * @param {number} sourceWidth - Source image width
 * @param {number} sourceHeight - Source image height
 * @param {number} targetWidth - Target width in inches
 * @param {number} targetHeight - Target height in inches
 * @returns {Object} Scaled dimensions {width, height, x, y} for centering
 */
export function calculateScaledDimensions(
  sourceWidth,
  sourceHeight,
  targetWidth = 4,
  targetHeight = 6
) {
  const sourceAspectRatio = sourceWidth / sourceHeight;
  const targetAspectRatio = targetWidth / targetHeight;

  let scaledWidth, scaledHeight;

  if (sourceAspectRatio > targetAspectRatio) {
    // Source is wider, scale to fit width
    scaledWidth = targetWidth;
    scaledHeight = targetWidth / sourceAspectRatio;
  } else {
    // Source is taller, scale to fit height
    scaledHeight = targetHeight;
    scaledWidth = targetHeight * sourceAspectRatio;
  }

  // Center on page
  const x = (targetWidth - scaledWidth) / 2;
  const y = (targetHeight - scaledHeight) / 2;

  return { width: scaledWidth, height: scaledHeight, x, y };
}

/**
 * Cleanup resources (remove temporary canvases from memory)
 * @param {HTMLCanvasElement[]} canvases - Array of canvases to cleanup
 */
export function cleanupCanvases(canvases) {
  canvases.forEach(canvas => {
    const context = canvas.getContext("2d");
    if (context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
    }
  });
}
